import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Dialog, DialogTitle, DialogContent, DialogActions, Icon, IconButton, Grid} from "@mui/material";
import { Button } from "@gpstrackit/react-controls";
import { NavLink } from "react-router-dom";
import { JsonView, allExpanded, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';

import CustomSelect from 'Components/Select';
import InputDate from 'Components/InputDate';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ChipStatus from 'Components/ChipStatus';
import ResendDealerAlertLog from '../resendDealerAlertLog';
import { NotificationManager } from "Components/Notifications";
import { HasPermission } from "Core/security";
import { getDateFormatted } from 'Core/data/Helpers';
import { getCompanies, getDevices } from './redux/saga';

import {
    reset,
} from './redux/slice';
import { openModalResendDealerAlertLog } from 'Redux/actions';

const DealerAlertLogsTab = (props) => {
    const { height, dealerId } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const user = useSelector(state => state.securityRedux.user);
    const openModalResend = useSelector(state => state.resendDealerAlertLogRedux?.openModal);

    const [columns, setColumns] = useState([
        {
            key: "id",
            field: "id",
            title: messages['dealer_alert_logs_id'],
            width: 80,
            show: true,
            locked: false,
            code: "dealer_alert_logs_id",
            render: (item) => <span>{item?.id}</span>,
            export: true,
            functionString: 'return row?.id',
        },
        {
            key: "timestamp",
            field: "timestamp",
            title: messages['dealer_alert_logs_timestamp'],
            width: 180,
            show: true,
            sort: true,
            locked: false,
            code: "dealer_alert_logs_timestamp",
            render: (item) => (
                <span>{item?.timestamp && getDateFormatted(item?.timestamp, "date", "datetime")}</span>
            ),
            export: true,
            resize: true,
            functionString: 'return row?.timestamp',
        },
        {
            key: "companyId",
            field: " company { id name }",
            title: messages['dealer_alert_logs_company_id'],
            width: 130,
            show: true,
            sort: true,
            locked: false,
            code: "dealer_alert_logs_company_id",
            export: true,
            resize: true,
            functionString: 'return row?.companyId',
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/accounts/overview/${item?.company?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.company?.name}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "deviceId",
            field: "unit { id label }",
            title: messages['dealer_alert_logs_device_id'],
            width: 130,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "dealer_alert_logs_device_id",
            export: true,
            functionString: 'return row?.deviceId',
            render: (item) => {
                return (
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="space-between"
                        className="context-menu-container"
                        wrap="nowrap"
                    >
                        <Grid item>
                            <NavLink to={`/app/devices/overview/${item?.unit?.id}`} style={{ color: 'black' }}>
                                <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.unit?.label}</span>
                            </NavLink>
                        </Grid>
                    </Grid>
                )
            }
        },
        {
            key: "alertType",
            field: "alertType",
            title: messages['dealer_alert_logs_alert_type'],
            width: 240,
            show: true,
            resize: true,
            locked: false,
            code: "dealer_alert_logs_alert_type",
            render: (item) => {
                try {
                    let payload = item?.payload;
                    if (typeof payload === 'string') {
                        payload = JSON.parse(payload);
                    }
                    const eventName = payload?.eventName;
                    return <span>{item?.alertType}{eventName ? ` (${eventName})` : ''}</span>;
                } catch {
                    return <span>{item?.alertType}</span>;
                }
            },
            export: true,
            functionString: 'return row?.alertType',
        },
        {
            key: "payload",
            field: "payload",
            title: messages['dealer_alert_logs_payload'],
            width: 100,
            show: true,
            locked: false,
            resize: true,
            code: "dealer_alert_logs_payload",
            render: (item) => (
                <button
                    onClick={() => setPayloadDialog({ open: true, payload: item?.payload })}
                    style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                >
                    {messages['dealer_alert_logs_view']}
                </button>
            ),
            export: false,
        },
        {
            key: "webhookStatus",
            field: "webhookStatus",
            title: messages['dealer_alert_logs_webhook_status'],
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "dealer_alert_logs_webhook_status",
            render: (item) => {
                if (!item?.webhookStatus) return <span>—</span>;
                return (
                    <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: item?.webhookStatus === 'Sent' ? '#c8e6c9' : '#ffcdd2',
                        color: item?.webhookStatus === 'Sent' ? '#2e7d32' : '#c62828',
                    }}>
                        {item?.webhookStatus}
                    </span>
                );
            },
            export: true,
            functionString: 'return row?.webhookStatus || "—"',
        },
        {
            key: "webhookSentOn",
            field: "webhookSentOn",
            title: messages['dealer_alert_logs_webhook_sent_on'],
            width: 180,
            show: true,
            locked: false,
            resize: true,
            code: "dealer_alert_logs_webhook_sent_on",
            render: (item) => (
                <span>{item?.webhookSentOn && getDateFormatted(item?.webhookSentOn, "date", "datetime")}</span>
            ),
            export: true,
            functionString: 'return row?.webhookSentOn',
        },
        {
            key: "webhookAttempts",
            field: "webhookAttempts",
            title: messages['dealer_alert_logs_webhook_attempts'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "dealer_alert_logs_webhook_attempts",
            render: (item) => <span>{item?.webhookAttempts || 0}</span>,
            export: true,
            functionString: 'return row?.webhookAttempts || 0',
        },
    ]);

    const [payloadDialog, setPayloadDialog] = useState({ open: false, payload: null });
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [companyFilter, setCompanyFilter] = useState(null);
    const [deviceFilter, setDeviceFilter] = useState(null);

    const graphqlConfig = useMemo(() => ({
        function: "searchDealerAlertLogs",
        sortType: "DealerAlertLogSortInput",
        conditionsType: "DealerAlertLogSearchInput",
        customQuery: "id companyId deviceId",
        dynamicQuery: true,
    }), []);

    const permanentConditions = useMemo(() => {
        const conditions = [{ fields: ["dealerId"], terms: [String(dealerId)], exact: true }];
        if (companyFilter?.id) {
            conditions.push({ fields: ["companyId"], terms: [String(companyFilter.id)], exact: true });
        }
        if (deviceFilter?.id) {
            conditions.push({ fields: ["deviceId"], terms: [String(deviceFilter.id)], exact: true });
        }
        if (startDate && endDate) {
            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);
            conditions.push({
                range: {
                    fields: ["timestamp"],
                    GTE: startDate.toISOString(),
                    LTE: endOfDay.toISOString(),
                },
            });
        }
        return conditions;
    }, [dealerId, companyFilter, deviceFilter, startDate, endDate]);

    const handleChangeConditions = useCallback((tags, conditions, total, newColumns, rows, sort) => {
        // Combine search conditions with permanent conditions (dealer filters)
        const mergedConditions = [...(conditions || []), ...permanentConditions];
        tableRef?.current?.getData({ newRows: rows, newSort: sort, newConditions: mergedConditions, newColumns });
    }, [permanentConditions]);

    const uiConfig = useMemo(() => ({
        hideFilters: true,
        hideSplit: true,
        hideAllInPagination: true,
        topHeight: 280,
        height: (height || 500) + "px",
    }), [height]);

    const exportConfig = useMemo(() => ({
        fileName: "dealer-alert-logs",
    }), []);

    const handleChangeColumns = useCallback((items) => {
        let currentColumns = items.map((item) => ({
            ...item,
            title: item.code ? messages[item.code] : item.title,
            render: item.render,
        }));
        setColumns(currentColumns);
    }, [messages]);

    const menuOptions = useMemo(() => [
        {
            id: "dealer_alert_logs_resend",
            label: messages['dealer_alert_logs_resend'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "dalertlog", "u"),
            action: (items) => {
                const currentItem = items.find(item => item.id);
                dispatch(openModalResendDealerAlertLog({ dealerId, log: currentItem }));
            }
        }
    ], [messages, user, dispatch, dealerId]);

    const getMenuOptions = useCallback(() => {
        return [messages['dealer_alert_logs_resend']];
    }, [messages]);

    const handleClickMenu = useCallback((event, item) => {
        if (event === messages['dealer_alert_logs_resend']) {
            if (HasPermission(user, "dalertlog", "u")) {
                dispatch(openModalResendDealerAlertLog({ dealerId, log: item }));
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['dealer_alert_logs_title'],
                    5000,
                );
            }
        }
    }, [messages, user, dispatch, dealerId]);

    const handleDateRangeChange = useCallback(({ start, end }) => {
        setStartDate(start);
        setEndDate(end);
    }, []);

    const companyFilterHandler = useCallback((newCompany) => {
        setCompanyFilter(newCompany);
        setDeviceFilter(null);
    }, []);

    const getCompaniesForSelect = useCallback((input, callback) => {
        if (!input) {
            callback([]);
            return;
        }
        getCompanies(input, dealerId).then((response) => {
            if (response?.result) {
                callback(response.result.items);
            }
        });
    }, [dealerId]);

    const getDevicesForSelect = useCallback((input, callback) => {
        if (!input) {
            callback([]);
            return;
        }
        getDevices(input, companyFilter?.id, dealerId).then((response) => {
            if (response?.result) {
                callback(response.result.items);
            }
        });
    }, [companyFilter, dealerId]);

    const applyFilters = useCallback(() => {
        tableRef?.current?.getData({ newPage: 0, newConditions: permanentConditions });
    }, [permanentConditions]);

    const renderExtraButton = useCallback(() => {
        return (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '5px', width: '100%' }}>
                <div style={{ flex: 1, minWidth: '150px', maxWidth: '250px' }}>
                    <CustomSelect
                        async
                        value={companyFilter}
                        onChange={companyFilterHandler}
                        label={messages['dealer_alert_logs_filter_company']}
                        loadOptions={getCompaniesForSelect}
                        getOptionLabel={e => e.name}
                        getOptionValue={e => e.id}
                        placeholder={messages['select']}
                    />
                </div>
                <div style={{ flex: 1, minWidth: '150px', maxWidth: '250px' }}>
                    <CustomSelect
                        async
                        value={deviceFilter}
                        onChange={setDeviceFilter}
                        label={messages['dealer_alert_logs_filter_device']}
                        loadOptions={getDevicesForSelect}
                        getOptionLabel={e => e.label}
                        getOptionValue={e => e.id}
                        placeholder={messages['select']}
                    />
                </div>
                <div style={{ flex: 1, minWidth: '200px', maxWidth: '280px' }}>
                    <InputDate
                        id="dealer-alert-logs-date-range"
                        type="range"
                        label={messages['dealer_alert_logs_filter_date_range']}
                        value={startDate}
                        valueEnd={endDate}
                        onChange={handleDateRangeChange}
                        maxDate={new Date()}
                        placeholder={messages['select']}
                    />
                </div>
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <Button className="btn-dark-blue" onClick={applyFilters}>
                        {messages['dealer_alert_logs_apply']}
                    </Button>
                </div>
            </div>
        );
    }, [messages, companyFilter, deviceFilter, startDate, endDate, handleDateRangeChange, getCompaniesForSelect, getDevicesForSelect, applyFilters, permanentConditions]);

    const customColumn = useCallback((item) => {
        return (
            <IconButton
                size="small"
                disabled={!HasPermission(user, "dalertlog", "u")}
                onClick={() => dispatch(openModalResendDealerAlertLog({ dealerId, log: item }))}
                title={messages['dealer_alert_logs_resend']}
            >
                <Icon style={{ fontSize: 18 }}>send</Icon>
            </IconButton>
        );
    }, [dispatch, dealerId, user, messages]);

    useEffect(() => {
        // Set initial date range: from start of week (Monday) to today
        const today = new Date();
        const startOfWeek = new Date(today);
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday = 1, Sunday = 0 → -6
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        setStartDate(startOfWeek);
        setEndDate(today);
    }, []);

    useEffect(() => {
        // Apply filters automatically when date range changes
        if (startDate && endDate) {
            tableRef?.current?.getData({ newPage: 0, newConditions: permanentConditions });
        }
    }, [startDate, endDate]);

    useEffect(() => {
        return () => {
            dispatch(reset());
        };
    }, [dispatch]);

    return (
        <div className="dealer-alert-logs-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                ref={tableRef}
                name="pm-dealer-alert-logs-tab"
                entity="dealer-alert-logs-tab"
                columns={columns}
                infiniteScroll={false}
                graphqlConfig={graphqlConfig}
                permanentConditions={permanentConditions}
                onChangeConditions={handleChangeConditions}
                uiConfig={uiConfig}
                exportConfig={exportConfig}
                onChangeColumns={handleChangeColumns}
                tableButtons={['export', 'orderColumn']}
                options={menuOptions}
                menuOptions={getMenuOptions}
                onClickMenu={handleClickMenu}
                extraButton={renderExtraButton}
                customColumn={customColumn}
                defaultsConfig={{
                    sort: { field: "timestamp", order: "DESC" },
                    rowsPerPage: 50,
                    valuesPerPage: [10, 25, 50, 100]
                }}
                searchConfig={{
                    disabled: true,
                }}
            />

            {/* Payload viewer modal */}
            <Dialog
                open={payloadDialog.open}
                onClose={() => setPayloadDialog({ open: false, payload: null })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{messages['dealer_alert_logs_payload_viewer']}</DialogTitle>
                <DialogContent dividers style={{ padding: '16px' }}>
                    {payloadDialog.payload ? (() => {
                        try {
                            let parsedPayload = payloadDialog.payload;
                            // Parse if it's a string (JSON serialized)
                            if (typeof parsedPayload === 'string') {
                                parsedPayload = JSON.parse(parsedPayload);
                            }
                            // Remove delivery property
                            const cleanPayload = JSON.parse(JSON.stringify(parsedPayload));
                            delete cleanPayload.delivery;
                            return <JsonView data={cleanPayload} shouldExpandNode={allExpanded} style={defaultStyles} />;
                        } catch (error) {
                            return <div>{messages['common_no_information_to_display']}</div>;
                        }
                    })() : <div>{messages['common_no_information_to_display']}</div>}
                </DialogContent>
                <DialogActions>
                    <Button
                        className="btn btn-cancel"
                        onClick={() => setPayloadDialog({ open: false, payload: null })}
                    >
                        {messages['common_close']}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Resend modal */}
            {openModalResend && <ResendDealerAlertLog tableRef={tableRef} />}
        </div>
    );
};

export default DealerAlertLogsTab;
